/**
 * Which surface a workbench tab belongs to.
 *
 * The editor owns one flat tab list, so a database result, a KV browser and an
 * open file all sat in the same strip regardless of which tool produced them.
 * Scoping lets the strip show only the tabs belonging to the surface in use:
 * pick Databases and you see the database explorer and its results, switch to
 * Queues and the strip becomes that tool's own.
 *
 * Scope is derived from the tab identity rather than stored, so it survives
 * reload and rehydrate without another piece of persisted state to keep
 * consistent.
 */

/** The scope shared by files, reviews and anything not owned by a tool. */
export const FILES_SCOPE = "files";

/** Result tab prefixes mapped to the explorer that opens them. */
const RESULT_PREFIX_SCOPES: ReadonlyArray<readonly [string, string]> = [
  ["database-query:", "explorer:databases"],
  ["database-table:", "explorer:databases"],
  ["workflow-instances:", "explorer:workflows"],
  ["workflow-instance:", "explorer:workflows"],
  ["workflow:", "explorer:workflows"],
  ["kv:", "explorer:kv"]
];

/**
 * Every result prefix the workbench mints. Kept beside the table above so a
 * new surface cannot add one without the omission being visible: an unmapped
 * prefix silently falls into the files scope, which is how a database table
 * ended up sitting next to an open file.
 */
export const KNOWN_RESULT_PREFIXES: readonly string[] = [
  "database-query:",
  "database-table:",
  "workflow:",
  "workflow-instances:",
  "workflow-instance:",
  "kv:"
];

/**
 * The scope owning `tabId`. Tool and explorer tabs own themselves; their result
 * tabs map onto them; everything else belongs to the files scope.
 */
export function tabScope(tabId: string): string {
  if (tabId.startsWith("tool:") || tabId.startsWith("explorer:")) return tabId;
  for (const [prefix, scope] of RESULT_PREFIX_SCOPES) {
    if (tabId.startsWith(prefix)) return scope;
  }
  return FILES_SCOPE;
}

/**
 * The scope a group is showing, taken from its active tab. An empty group
 * shows the files scope so it can still receive dropped file tabs.
 */
export function activeScopeOf(activeTabId: string | null | undefined): string {
  return activeTabId ? tabScope(activeTabId) : FILES_SCOPE;
}

/** Whether `tabId` should be visible while `scope` is active. */
export function isTabInScope(tabId: string, scope: string): boolean {
  return tabScope(tabId) === scope;
}
